import { backend_api_v2 } from '/src/utils/apiv2'
import { plannable_units } from "@/utils/subassemblies";
import base from "./base"
import bus from '/src/utils/event_bus'
import _ from "lodash"
// import router from '/src/router'
import { defineStore } from 'pinia'
import { useConstructiblesStore } from "/src/stores/constructibles"
import { useSelectedStore } from "/src/stores/selected"
import { useAppStore } from "/src/stores/app"
import { useTranslationsStore } from "/src/stores/translations"
import { countsFromStored, projectPhases } from "/src/utils/phase_keys"
import {
    buildCountIndex, columnKey, countKey, countersFromActuals, countMismatches,
} from "/src/utils/quantity_counts"


// How many undo transactions to keep.
const UNDO_DEPTH = 100

// The four arrays that hold the matrix, and the shape it is saved in. Order does
// not matter here; completeness does.
const COUNT_STATE_KEYS = [
    "assd_batch_counts", "assd_phase_counts",
    "modd_batch_counts", "modd_phase_counts",
]

// While any of these is loading, the designs and batches the saved plan is checked
// against are incomplete, so the plan cannot be judged. See staleCounts.
const PLAN_GATING_LOADERS = [
    "project_resources", "linked_stdlibs",
    "AssemblyDesigns", "ModuleDesigns", "Batches", "stdStdlibAssemblyDesigns",
]

// The drift check is judged against the units that exist too, so it has to wait
// for them as well. Mid-load the store holds no assemblies and every planned unit
// would read as missing from production. See countsDrift.
const DRIFT_GATING_LOADERS = [...PLAN_GATING_LOADERS, "Assemblies", "Modules"]

export const useQuantitiesStore = defineStore('quantities', {
    state: () => ({
        // Assemblies
        assd_batch_counts: [],
        assd_phase_counts: [],

        // Modules
        modd_batch_counts: [],
        modd_phase_counts: [],

        // State
        generating: false,

        // The plan as the API last answered or stored it
        // (`company/projects/<uuid>/quantities/`): the four lists, plus which
        // levels have a saved plan. The matrix is rebuilt from it whenever the
        // designs, batches or units change. Null until it is loaded.
        saved_plan: null,
        plan_project: null,
        // The splitter's counts, from the same answer: the target of each
        // design by name per level, the phase/batch split the IFC carried,
        // and the panels of each module design.
        split_targets: { assembly: {}, module: {} },
        split_tree: { assembly: {}, module: {} },
        split_compositions: {},
        // The project IFCs that were split: units, new units, counting mode.
        split_sources: [],

        // Grid selection. Replaces the old hover_row / hover_cell store state:
        // controls now belong to the SELECTED cell, which is what makes keyboard
        // navigation possible at all (row highlighting is plain CSS :hover).
        sel_row: null,      // design uuid
        sel_col: null,      // phase uuid, or batch uuid
        editing: false,     // the selected cell is in text-entry mode
        editing_seed: null, // first digit typed, so "1" then "2" gives 12
        // Committing an edit with Enter/Tab should also move the selection, but the
        // cell owns the value and the grid owns the movement. The cell parks its
        // intent here and the grid composable carries it out.
        move_request: null,

        // Unsaved-changes tracking for autosave.
        dirty: false,
        saving: false,
        last_saved_at: null,

        // Undo/redo. Every count write records its before/after here, so a
        // mistyped number or a fill-down over the wrong column is recoverable.
        undo_stack: [],
        redo_stack: [],
        open_group: null,
        applying_history: false,
    }),
    getters: {
        // INDEXES
        // The four count arrays stay the source of truth and the save format, but
        // every read goes through these Maps. Without them each cell ran a linear
        // .find() over the whole array from inside its own computed, and — because
        // Vue tracks every element visited by .find() — accumulated thousands of
        // dependencies. Measured at 400 designs x 20 phases: first paint went from
        // ~24-37s to ~40ms, a single edit from ~1s to a few ms.
        // Exactly what is written to project.meta.quantities. Every writer takes
        // the payload from here. A row carries its phase by uuid, in memory and
        // stored alike, so a rename never has to rewrite the counts.
        countsPayload: state => Object.fromEntries(
            COUNT_STATE_KEYS.map(key => [key, state[key]])
        ),

        assdPhaseIndex: state => buildCountIndex(state.assd_phase_counts),
        assdBatchIndex: state => buildCountIndex(state.assd_batch_counts),
        moddPhaseIndex: state => buildCountIndex(state.modd_phase_counts),
        moddBatchIndex: state => buildCountIndex(state.modd_batch_counts),

        // COUNT GETTERS

        // This are the row sums (right column)
        getAssdProjectTotal: getProjectTotalFactory("assdPhaseIndex"),
        getModdProjectTotal: getProjectTotalFactory("moddPhaseIndex"),

        // This is a simple count
        getAssdBatchCount: getCountFactory("assdBatchIndex"),
        getAssdPhaseCount: getCountFactory("assdPhaseIndex"),
        getModdBatchCount: getCountFactory("moddBatchIndex"),
        getModdPhaseCount: getCountFactory("moddPhaseIndex"),

        // This is for the totals rows: one pass over the counts per mutation,
        // instead of copying all four arrays once per column.
        columnTotals(state) {
            const constructibles_store = useConstructiblesStore()
            const area_by_design = constructibles_store.brutoAreaByDesign
            // A count left on a subassembly has no row in the matrix, and its
            // units come with the panel's, which are counted already.
            const skip = constructibles_store.nonQuantityDesignUuids

            const assembly = new Map()
            const module = new Map()
            const all = new Map()

            addToColumnTotals(assembly, state.assd_phase_counts, area_by_design, skip)
            addToColumnTotals(assembly, state.assd_batch_counts, area_by_design, skip)
            addToColumnTotals(module, state.modd_phase_counts, area_by_design, skip)
            addToColumnTotals(module, state.modd_batch_counts, area_by_design, skip)

            for (const source of [assembly, module]) {
                for (const [key, entry] of source) {
                    const total = all.get(key)
                    if (total) {
                        total.count += entry.count
                        total.m2 += entry.m2
                    } else {
                        all.set(key, { count: entry.count, m2: entry.m2 })
                    }
                }
            }

            return { assembly, module, all }
        },

        getColumnTotal() {
            const totals = this.columnTotals
            return (level, phase, batch) =>
                totals[level]?.get(columnKey(phase, batch?.uuid))?.count || 0
        },

        getColumnTotalM2() {
            const totals = this.columnTotals
            return (level, phase, batch) =>
                totals[level]?.get(columnKey(phase, batch?.uuid))?.m2 || 0
        },

        // Plan vs floor. The matrix is a PLAN: it is written to the project a couple
        // of seconds after you stop typing, and "Saved" says exactly that and
        // nothing more — the units it describes only exist once Generate has run.
        // Users read "Saved" as "this is what the factory has", which it is not.
        // This getter is the missing third fact: how far the saved plan is from the
        // units that exist.
        //
        // It reports EXACTLY what Generate would do — same (design, batch) slots,
        // batch counts only, stale rows ignored — because the whole point is to
        // point at a button that then acts. A chip promising a difference that
        // Generate refuses to touch would be worse than no chip at all. One
        // consequence to know about: units belonging to a design the plan does not
        // name are invisible here, because the generator does not delete them
        // either.
        //   level = "assembly" | "module" | "all"
        countsDrift() {
            const constructibles_store = useConstructiblesStore()
            const app_store = useAppStore()
            return (level) => {
                const levels = level === "module" ? ["module"]
                    : level === "assembly" ? ["assembly"]
                        : ["assembly", "module"]
                const total = {
                    known: false, to_create: 0, to_delete: 0, units: 0, slots: 0,
                    by_level: {},
                }

                // Same reason as staleCounts: never judge the plan until the rows
                // it is judged against have arrived.
                if (app_store.loader_is_active(DRIFT_GATING_LOADERS)) return total
                total.known = true

                for (const lev of levels) {
                    const batch_key = lev === "module" ? "modd_batch_counts" : "assd_batch_counts"
                    const stale = this.staleCounts(lev).keys[batch_key]
                    const actuals = constructibles_store.actualsCountByDesignBatch[lev]
                    const level_drift = { to_create: 0, to_delete: 0, units: 0, slots: 0 }

                    for (const c of this[batch_key]) {
                        if (stale.has(countKey(c.design, c.phase, c.batch))) continue
                        const existent = actuals.get(`${c.design}|${c.batch ?? ""}`) || 0
                        const diff = (c.count || 0) - existent
                        if (diff === 0) continue
                        level_drift.slots += 1
                        if (diff > 0) level_drift.to_create += diff
                        else level_drift.to_delete -= diff
                    }
                    level_drift.units = level_drift.to_create + level_drift.to_delete

                    total.by_level[lev] = level_drift
                    total.to_create += level_drift.to_create
                    total.to_delete += level_drift.to_delete
                    total.slots += level_drift.slots
                    total.units += level_drift.units
                }
                return total
            }
        },

        // Per-design target vs assigned for the matrix currently on screen, plus the
        // project roll-up. Drives the status bar and the "balance" actions, and is
        // one pass over the counts rather than a scan per row.
        //   scope = { mode: "phases" } | { mode: "batches", phase, batch_uuids }
        matrixBalance() {
            return (scope) => {
                const constructibles_store = useConstructiblesStore()
                const designs = constructibles_store.quantityDesignsFromLevel("all")
                const batch_uuids = scope.batch_uuids ? new Set(scope.batch_uuids) : null
                // The target is keyed by design NAME, so every revision of a name
                // reads the same number. Only the last revision may claim it, or a
                // design with three revisions counted its target three times.
                const last_revisions = new Set(
                    constructibles_store.quantityDesignsFromLevelOnlyLastRevisions("all")
                        .map(d => d.uuid)
                )

                const rows = []
                let target_total = 0
                let assigned_total = 0
                let off = 0
                let has_any_target = false

                for (const design of designs) {
                    const is_module = design.level === "module"
                    const phase_index = is_module ? this.moddPhaseIndex : this.assdPhaseIndex
                    const batch_index = is_module ? this.moddBatchIndex : this.assdBatchIndex

                    let target = 0
                    let assigned = 0
                    // A project that was never split has no targets at all, and a
                    // design added by hand has none either. Comparing those against
                    // a target of 0 marked every row as over budget, so a row with
                    // no target is not judged.
                    let has_target = true
                    if (scope.mode === "batches") {
                        // Here the target is the phase count: the planner's own
                        // number, so it always means something.
                        target = phase_index.get(countKey(design.uuid, scope.phase, undefined))?.count || 0
                        if (batch_uuids) {
                            for (const uuid of batch_uuids) {
                                assigned += batch_index.get(countKey(design.uuid, scope.phase, uuid))?.count || 0
                            }
                        }
                        // Rows with nothing on either side are not part of this phase.
                        if (!target && !assigned) continue
                    } else {
                        // Absent is not the same as zero, so `in` rather than `|| 0`.
                        const own = this.targetOf(design)
                        has_target = last_revisions.has(design.uuid) && own !== undefined
                        target = has_target ? own : 0
                        for (const c of phase_index.values()) {
                            if (c.design === design.uuid) assigned += c.count
                        }
                        if (!target && !assigned) continue
                    }

                    target_total += target
                    assigned_total += assigned
                    if (has_target) {
                        has_any_target = true
                        if (target !== assigned) off += 1
                    }
                    rows.push({
                        design, target, assigned, has_target,
                        diff: assigned - target,
                    })
                }

                return { rows, target_total, assigned_total, off, has_any_target }
            }
        },

        // The splitter's target of a design, by its name within its level, or
        // undefined when the IFC never named it. Absent is not zero.
        targetOf: (state) => (design) => {
            if (!design) return undefined
            const targets = state.split_targets?.[design.level === "module" ? "module" : "assembly"] || {}
            return design.name in targets ? targets[design.name] : undefined
        },

        // A level total is only comparable to its target when every design in it
        // has one. Mixing designs that carry a target with designs that do not
        // makes the pair meaningless: the assigned side counts all of them and the
        // target side only some, so the row reads as a mismatch that is not there.
        targetCoverageByLevel() {
            return level => {
                const constructibles_store = useConstructiblesStore()
                const designs = constructibles_store
                    .quantityDesignsFromLevelOnlyLastRevisions(level)
                let with_target = 0
                for (const design of designs) {
                    if (this.targetOf(design) !== undefined) with_target += 1
                }
                return {
                    total: designs.length,
                    with_target,
                    complete: designs.length > 0 && with_target === designs.length,
                }
            }
        },

        // STALE COUNTS
        // The matrix is kept keyed by design and batch uuid, so while it is open
        // it can name what was deleted meanwhile: designs get deleted and
        // re-generated, batches get removed, and the saved numbers keep naming the
        // old rows. Those rows are invisible in the matrix (there is no design to
        // draw them on) but the generator still turns them into orders, and the
        // backend answers the whole POST with a 404 — so nothing is ever generated
        // and, because the saved plan is only cleared after a SUCCESSFUL generate,
        // the project stays stuck. This is what has to be dropped.
        staleCounts() {
            const constructibles_store = useConstructiblesStore()
            const app_store = useAppStore()
            return (level) => {
                const keys = {}
                const empty = { entries: 0, units: 0, rows: [], keys }

                const is_module = level === "module"
                const batch_key = is_module ? "modd_batch_counts" : "assd_batch_counts"
                const phase_key = is_module ? "modd_phase_counts" : "assd_phase_counts"
                keys[batch_key] = new Set()
                keys[phase_key] = new Set()

                // Mid-load the designs and batches are not there yet, so every
                // single entry would read as stale. Never judge the plan until the
                // rows it is judged against have arrived. Only these loaders can
                // do that: gating on app_store.loading instead would silently
                // disable the check whenever anything else (reports, packages) is
                // in flight.
                if (app_store.loader_is_active(PLAN_GATING_LOADERS)) return empty

                const designs = new Set(
                    constructibles_store.quantityDesignsFromLevel(level).map(d => d.uuid)
                )
                // A design that became a subassembly still exists, so it is named
                // as such rather than as a deleted one.
                const subassemblies = constructibles_store.nonQuantityDesignUuids
                const batches = constructibles_store.batchesByUuid

                // Grouped by design: one deleted design usually leaves an entry per
                // phase and per batch, and listing 40 of them says no more than
                // listing the design once with its total.
                const rows = new Map()
                let entries = 0
                let units = 0

                for (const state_key of [batch_key, phase_key]) {
                    // Only the batch counts turn into units: the phase counts are
                    // the planner's target for the same numbers, so adding both
                    // would report every unit twice.
                    const counts_units = state_key === batch_key
                    for (const c of this[state_key]) {
                        // The design is checked against its own level: the backend
                        // resolves it against AssemblyDesign or ModuleDesign, so a
                        // uuid from the other level is just as missing.
                        const subassembly = subassemblies.has(c.design)
                        const design_gone = !designs.has(c.design) && !subassembly
                        const batch_gone = c.batch != undefined && !batches.has(c.batch)
                        if (!subassembly && !design_gone && !batch_gone) continue

                        keys[state_key].add(countKey(c.design, c.phase, c.batch))
                        entries += 1
                        const row_units = counts_units ? c.count : 0
                        units += row_units

                        // A design that is gone and a batch that is gone are two
                        // different things to say, so they are two rows.
                        const row_key = subassembly ? `s|${c.design}`
                            : design_gone ? `d|${c.design}` : `b|${c.design}`
                        const row = rows.get(row_key)
                        if (row) {
                            row.entries += 1
                            row.units += row_units
                        } else {
                            rows.set(row_key, {
                                key: row_key,
                                design: c.design,
                                design_gone,
                                subassembly,
                                entries: 1,
                                units: row_units,
                            })
                        }
                    }
                }

                return {
                    entries,
                    units,
                    rows: [...rows.values()].sort((a, b) => b.units - a.units),
                    keys,
                }
            }
        },

        getProjectFullCountTotalByLevel() {
            return level => {
                const constructibles_store = useConstructiblesStore()
                // Only the last revision of each design, otherwise a design with
                // three revisions counted its target three times.
                const designs = constructibles_store.quantityDesignsFromLevelOnlyLastRevisions(level)
                return designs.reduce((a, d) => a + (this.targetOf(d) || 0), 0)
            }
        },


        // FLAGS
        // Locked while the project's designs are being updated: a run creates
        // revisions and moves units under the matrix (the split included).
        lockFullMatrix() {
            return useSelectedStore().designUpdateLocksMatrix
        },
        lockMatrixNumbers() {
            const app_store = useAppStore()
            let lock = false
            // lock also if system is loading assemblies / designs
            // Otherwise user gets frustrated because he sees the zeroes lol jaja
            if (app_store.loading) lock = true

            if (this.lockFullMatrix) lock = true

            return lock

        }
    },
    actions: {
        // Assemblies
        sumOneAssdToBatch: sumFactory("assd_batch_counts", "assdBatchIndex", +1),
        subOneAssdToBatch: sumFactory("assd_batch_counts", "assdBatchIndex", -1),
        putAssdCountOnBatch: putCountFactory("assd_batch_counts", "assdBatchIndex"),

        sumOneAssdToPhase: sumFactory("assd_phase_counts", "assdPhaseIndex", +1),
        subOneAssdToPhase: sumFactory("assd_phase_counts", "assdPhaseIndex", -1),
        putAssdCountOnPhase: putCountFactory("assd_phase_counts", "assdPhaseIndex"),

        setAssdBatchCounts: base.actions.setter("assd_batch_counts"),
        setAssdPhaseCounts: base.actions.setter("assd_phase_counts"),

        // Modules
        sumOneModdToBatch: sumFactory("modd_batch_counts", "moddBatchIndex", +1),
        subOneModdToBatch: sumFactory("modd_batch_counts", "moddBatchIndex", -1),
        putModdCountOnBatch: putCountFactory("modd_batch_counts", "moddBatchIndex"),

        sumOneModdToPhase: sumFactory("modd_phase_counts", "moddPhaseIndex", +1),
        subOneModdToPhase: sumFactory("modd_phase_counts", "moddPhaseIndex", -1),
        putModdCountOnPhase: putCountFactory("modd_phase_counts", "moddPhaseIndex"),

        setModdBatchCounts: base.actions.setter("modd_batch_counts"),
        setModdPhaseCounts: base.actions.setter("modd_phase_counts"),

        // Create every count object a matrix needs, in ONE mutation per array.
        // Previously each cell pushed its own row from an immediate watcher during
        // render, which forced a second full re-evaluation of the whole matrix.
        seedPhaseCounts({ designs, phases }) {
            const slots = (phases || []).map(phase => ({ phase, batch: undefined }))
            this.seedCounts(designs, slots, "assd_phase_counts", "modd_phase_counts")
        },
        seedBatchCounts({ designs, phase, batches }) {
            const slots = (batches || []).map(b => ({ phase: b.phase_uuid ?? phase, batch: b.uuid }))
            this.seedCounts(designs, slots, "assd_batch_counts", "modd_batch_counts")
        },
        seedCounts(designs, slots, assd_key, modd_key) {
            if (!designs?.length || !slots.length) return
            const additions = { [assd_key]: [], [modd_key]: [] }
            const seen = {
                [assd_key]: new Set(this[assd_key].map(c => countKey(c.design, c.phase, c.batch))),
                [modd_key]: new Set(this[modd_key].map(c => countKey(c.design, c.phase, c.batch))),
            }
            for (const design of designs) {
                if (!design?.uuid) continue
                const key = design.level == "module" ? modd_key : assd_key
                for (const slot of slots) {
                    const k = countKey(design.uuid, slot.phase, slot.batch)
                    if (seen[key].has(k)) continue
                    seen[key].add(k)
                    additions[key].push({
                        phase: slot.phase, batch: slot.batch, design: design.uuid, count: 0,
                    })
                }
            }
            for (const key of [assd_key, modd_key]) {
                if (additions[key].length) this[key] = this[key].concat(additions[key])
            }
        },

        // State
        setGenerating: base.actions.setter("generating"),

        // Selection
        selectCell(row, col) {
            this.sel_row = row
            this.sel_col = col
            this.editing = false
            this.editing_seed = null
        },
        clearSelection() {
            this.sel_row = null
            this.sel_col = null
            this.editing = false
            this.editing_seed = null
        },
        // Typing a digit on a selected cell opens the editor seeded with it,
        // rather than writing the digit and then opening an empty editor.
        beginEdit(seed = null) {
            this.editing = true
            this.editing_seed = seed
        },
        endEdit() {
            this.editing = false
            this.editing_seed = null
        },
        requestMove(dr, dc) {
            // The counter makes each request distinct, so two moves in the same
            // direction both fire.
            this.move_request = { dr, dc, n: (this.move_request?.n || 0) + 1 }
        },
        clearMoveRequest() {
            this.move_request = null
        },
        isSelected(row, col) {
            return this.sel_row === row && this.sel_col === col
        },

        // Dirty tracking. Only user edits mark the matrix dirty — seeding and
        // loading must not, or autosave would fire on every page open.
        markDirty() {
            this.dirty = true
        },
        markSaved() {
            this.dirty = false
            this.last_saved_at = Date.now()
        },
        setSaving(value) {
            this.saving = !!value
        },

        // --- undo / redo -----------------------------------------------------
        // One undo entry is a TRANSACTION: an array of edits that were made by a
        // single gesture. A plain cell edit is a transaction of one; fill down,
        // paste and allocate-by-prefix wrap many so they undo in one step.
        // Each edit is {state_key, index_key, design, phase, batch, from, to},
        // so replaying it in either direction is the same code with from/to
        // swapped.
        recordEdit(edit) {
            if (this.applying_history) return
            if (this.open_group) {
                this.open_group.push(edit)
            } else {
                this.undo_stack.push([edit])
                if (this.undo_stack.length > UNDO_DEPTH) this.undo_stack.shift()
                this.redo_stack = []
            }
        },
        beginEditGroup() {
            this.open_group = []
        },
        endEditGroup() {
            const group = this.open_group
            this.open_group = null
            if (!group?.length) return
            this.undo_stack.push(group)
            if (this.undo_stack.length > UNDO_DEPTH) this.undo_stack.shift()
            this.redo_stack = []
        },
        applyEdit(edit, value) {
            this.applying_history = true
            try {
                const existing = this[edit.index_key].get(
                    countKey(edit.design, edit.phase, edit.batch)
                )
                if (existing) existing.count = value
                else this[edit.state_key].push({
                    phase: edit.phase, batch: edit.batch, design: edit.design, count: value,
                })
            } finally {
                this.applying_history = false
            }
            this.dirty = true
        },
        undo() {
            const group = this.undo_stack.pop()
            if (!group) return 0
            // Reverse order, so overlapping writes land back on the older value.
            for (const edit of [...group].reverse()) this.applyEdit(edit, edit.from)
            this.redo_stack.push(group)
            return group.length
        },
        redo() {
            const group = this.redo_stack.pop()
            if (!group) return 0
            for (const edit of group) this.applyEdit(edit, edit.to)
            this.undo_stack.push(group)
            return group.length
        },
        clearHistory() {
            this.undo_stack = []
            this.redo_stack = []
            this.open_group = null
        },

        generateAssembliesFromCounts: generatorFactory(
            "assembly",
            "assd_batch_counts",
            "assd_phase_counts",
            "assemblies",
        ),
        generateModulesFromCounts: generatorFactory(
            "module",
            "modd_batch_counts",
            "modd_phase_counts",
            "modules",
        ),

        // What Generate would do with these orders, without doing it: the
        // units to create and delete, the IFC identity figures, and each IFC
        // building and storey of the new units with its default action.
        previewGeneration(level, orders) {
            const project = useSelectedStore().selected_project
            return backend_api_v2.post(`company/projects/${project.uuid}/quantities/generate/preview/`,
                { level, orders }).then(({ data }) => data)
        },

        // Forget the counts that name a design or a batch that no longer exists.
        // Not undoable, and it does not need to be: there is no row to bring them
        // back to. Marking dirty is enough to persist it — autosave owns the write.
        dropStaleCounts(level) {
            const stale = this.staleCounts(level)
            if (!stale.entries) return 0
            for (const [state_key, keys] of Object.entries(stale.keys)) {
                if (!keys.size) continue
                this[state_key] = this[state_key].filter(
                    c => !keys.has(countKey(c.design, c.phase, c.batch))
                )
            }
            this.markDirty()
            return stale.entries
        },

        cleanAssemblyCounters() {
            const constructibles_store = useConstructiblesStore()
            // The panels' units only: a subassembly's units come with them and
            // are not planned, so counting them wrote rows the matrix cannot
            // show, into the totals and the Generate dialog.
            const { batch_counts, phase_counts } = countersFromActuals(
                plannable_units(constructibles_store.assemblies,
                    constructibles_store.nonQuantityDesignUuids),
                constructibles_store.batchesByUuid,
            )
            this.setAssdBatchCounts(batch_counts);
            this.setAssdPhaseCounts(phase_counts);
        },
        cleanModuleCounters() {
            const constructibles_store = useConstructiblesStore()
            const { batch_counts, phase_counts } = countersFromActuals(
                constructibles_store.modules,
                constructibles_store.batchesByUuid,
            )
            this.setModdBatchCounts(batch_counts);
            this.setModdPhaseCounts(phase_counts);
        },
        cleanAllCounters() {
            this.cleanAssemblyCounters()
            this.cleanModuleCounters()
        },

        // Called when the selected project changes. Everything else the project
        // owns is emptied at that moment; the counts were not, and the seeder that
        // refills them is debounced by 3 s — so for a second or two the matrix held
        // one project's plan beside another project's units. Nothing showed it
        // before the drift chip, which would have reported the whole plan as
        // missing from production. The history and the dirty flag go with them: an
        // undo would write a value keyed by a phase of the old project, and a
        // pending autosave would write the old plan into the new project's meta.
        resetCountsForProject() {
            this.saved_plan = null
            this.plan_project = null
            this.split_targets = { assembly: {}, module: {} }
            this.split_tree = { assembly: {}, module: {} }
            this.split_compositions = {}
            this.split_sources = []
            for (const state_key of COUNT_STATE_KEYS) this[state_key] = []
            this.clearHistory()
            // Not markSaved(): nothing was written, so last_saved_at must not move.
            this.dirty = false
        },

        // Distribute the phase-level counts of a single phase into its batches
        // by matching design names against batch prefixes (longest prefix wins).
        // The matched batch gets the whole phase count; the rest are zeroed, so
        // the batch sum equals the phase total. Returns the set of design names
        // that matched no batch prefix.
        allocateByPrefixOnPhase(phase) {
            const constructibles_store = useConstructiblesStore()
            const designsByUuid = constructibles_store.designsByUuid
            const excluded = constructibles_store.nonQuantityDesignUuids
            const batches_in_phase = constructibles_store.batches
                .filter(b => b.phase_uuid === phase)

            const unmatched = new Set()

            const match_batch = (name) => {
                let best = null
                let best_len = -1
                batches_in_phase.forEach(b => {
                    (b.meta?.prefixes || []).forEach(prefix => {
                        if (prefix && name.startsWith(prefix) && prefix.length > best_len) {
                            best = b
                            best_len = prefix.length
                        }
                    })
                })
                return best
            }

            const allocate = (phaseCounts, putOnBatch) => {
                phaseCounts
                    .filter(c => c.phase === phase && c.count > 0)
                    .forEach(c => {
                        const design = designsByUuid.get(c.design)
                        if (!design || excluded.has(c.design)) return
                        const target = match_batch(design.name)
                        if (!target) {
                            unmatched.add(design.name)
                            return
                        }
                        batches_in_phase.forEach(b => {
                            const count = b.uuid === target.uuid ? c.count : 0
                            putOnBatch.call(this, { phase, batch: b, design: c.design, count })
                        })
                    })
            }

            // One gesture, so it undoes in one step.
            this.beginEditGroup()
            try {
                allocate(this.assd_phase_counts, this.putAssdCountOnBatch)
                allocate(this.modd_phase_counts, this.putModdCountOnBatch)
            } finally {
                this.endEditGroup()
            }

            return unmatched
        },
        // Make a design's columns add up to its target, without inventing an
        // allocation the planner did not ask for: the difference goes onto the
        // columns that already carry a number, proportionally, and the remainder
        // lands on the largest one. Only if every column is empty does it all go on
        // the first. Returns the number of designs it changed.
        balanceRows(scope, rows) {
            if (!rows?.length) return 0
            const columns = scope.mode === "batches" ? scope.batch_uuids : scope.phases
            if (!columns?.length) return 0

            let changed = 0
            this.beginEditGroup()
            try {
                for (const row of rows) {
                    if (row.diff === 0) continue
                    const is_module = row.design.level === "module"
                    const index = scope.mode === "batches"
                        ? (is_module ? this.moddBatchIndex : this.assdBatchIndex)
                        : (is_module ? this.moddPhaseIndex : this.assdPhaseIndex)

                    const current = columns.map(column => {
                        const key = scope.mode === "batches"
                            ? countKey(row.design.uuid, scope.phase, column)
                            : countKey(row.design.uuid, column, undefined)
                        return { column, count: index.get(key)?.count || 0 }
                    })

                    const assigned = current.reduce((a, c) => a + c.count, 0)
                    const wanted = row.target
                    const next = distributeTotal(current, wanted, assigned)

                    next.forEach(({ column, count }, i) => {
                        if (count === current[i].count) return
                        this.writeScopedCount(scope, row.design, column, count)
                    })
                    changed += 1
                }
            } finally {
                this.endEditGroup()
            }
            return changed
        },
        writeScopedCount(scope, design, column, count) {
            const is_module = design.level === "module"
            if (scope.mode === "batches") {
                const payload = { phase: scope.phase, batch: column, design, count }
                if (is_module) this.putModdCountOnBatch(payload)
                else this.putAssdCountOnBatch(payload)
            } else {
                const payload = { phase: column, design, count }
                if (is_module) this.putModdCountOnPhase(payload)
                else this.putAssdCountOnPhase(payload)
            }
        },

        assemblyCountersFromSaveOrClean() {
            this.countersFromSaveOrClean("assd")
        },
        moduleCountersFromSaveOrClean() {
            this.countersFromSaveOrClean("modd")
        },
        // Load one level's saved counts, or rebuild them from the units when the
        // project has none saved. Rows of a phase the project no longer has are
        // left out, so the next save drops them.
        countersFromSaveOrClean(level) {
            const project = useSelectedStore().selected_project
            const saved = this.saved_plan
            // Not loaded yet (or loaded for another project): rebuilding from
            // the units now would show them as the plan.
            if (!saved || !project || this.plan_project !== project.uuid) return
            const batch_key = `${level}_batch_counts`
            const phase_key = `${level}_phase_counts`
            const setters = level === "assd"
                ? ["setAssdBatchCounts", "setAssdPhaseCounts"]
                : ["setModdBatchCounts", "setModdPhaseCounts"]
            if (saved.saved?.[level === "assd" ? "assembly" : "module"]) {
                this[setters[0]](countsFromStored(saved[batch_key] || [], project))
                this[setters[1]](countsFromStored(saved[phase_key] || [], project))
            } else if (level === "assd") this.cleanAssemblyCounters()
            else this.cleanModuleCounters()
        },

        // The plan and the splitter's counts of a project, from the API.
        loadPlan(project_uuid) {
            if (!project_uuid || project_uuid === "all") return Promise.resolve()
            return backend_api_v2.get(`company/projects/${project_uuid}/quantities/`)
                .then(({ data }) => {
                    // A later project change already moved on.
                    if (useSelectedStore().selected_project?.uuid !== project_uuid) return
                    const { targets, split_tree, compositions, sources, ...plan } = data
                    this.saved_plan = plan
                    this.plan_project = project_uuid
                    this.split_targets = targets || { assembly: {}, module: {} }
                    this.split_tree = split_tree || { assembly: {}, module: {} }
                    this.split_compositions = compositions || {}
                    this.split_sources = sources || []
                })
        },

        // Only the splitter's counts, leaving the plan being edited alone: what
        // the quantities view refreshes when it opens, after a split ran.
        loadSplit(project_uuid) {
            if (!project_uuid || project_uuid === "all") return Promise.resolve()
            return backend_api_v2.get(`company/projects/${project_uuid}/quantities/`)
                .then(({ data }) => {
                    if (useSelectedStore().selected_project?.uuid !== project_uuid) return
                    this.split_targets = data.targets || { assembly: {}, module: {} }
                    this.split_tree = data.split_tree || { assembly: {}, module: {} }
                    this.split_compositions = data.compositions || {}
                    this.split_sources = data.sources || []
                })
        },

        // Store the matrix on the project. Only the difference is written.
        savePlan() {
            const project = useSelectedStore().selected_project
            if (!project?.uuid) return Promise.resolve()
            const payload = this.countsPayload
            return backend_api_v2.put(`company/projects/${project.uuid}/quantities/`, payload)
                .then(({ data }) => {
                    const levels = data?.levels || ["assembly", "module"]
                    this.saved_plan = {
                        ...payload,
                        saved: {
                            assembly: levels.includes("assembly") || !!this.saved_plan?.saved?.assembly,
                            module: levels.includes("module") || !!this.saved_plan?.saved?.module,
                        },
                    }
                    this.plan_project = project.uuid
                    // The menu's Production counts figures follow the saved plan
                    // once the matrix is left.
                    useSelectedStore().loadAttention()
                    return data
                })
        },

        // Forget a level's saved plan (or both), so the matrix shows the units.
        forgetPlan(levels = ["assembly", "module"]) {
            if (!this.saved_plan) return
            const plan = { ...this.saved_plan, saved: { ...(this.saved_plan.saved || {}) } }
            for (const level of levels) {
                const prefix = level === "module" ? "modd" : "assd"
                plan[`${prefix}_phase_counts`] = []
                plan[`${prefix}_batch_counts`] = []
                plan.saved[level] = false
            }
            this.saved_plan = plan
        },

        // Reset quantities: delete the saved plan of both levels.
        clearPlan() {
            const project = useSelectedStore().selected_project
            if (!project?.uuid) return Promise.resolve()
            return backend_api_v2.delete(`company/projects/${project.uuid}/quantities/`)
                .then(() => this.forgetPlan())
        },
    },
})


export const assemblyCountersFromSaveOrClean = _.debounce(function () {
    const quantities_store = useQuantitiesStore()
    quantities_store.assemblyCountersFromSaveOrClean()
}, 3000)

export const moduleCountersFromSaveOrClean = _.debounce(function () {
    const quantities_store = useQuantitiesStore()
    quantities_store.moduleCountersFromSaveOrClean()
}, 3000)

export const allCountersFromSaveOrClean = _.debounce(function () {
    const quantities_store = useQuantitiesStore()
    quantities_store.assemblyCountersFromSaveOrClean()
    quantities_store.moduleCountersFromSaveOrClean()
}, 3000)


// Spread `wanted` over the columns, keeping the existing shape of the allocation.
// Columns already carrying a number keep their share; the rounding remainder goes
// to the biggest one. If nothing is allocated yet, it all lands on the first column
// rather than being smeared evenly across columns nobody chose.
function distributeTotal(current, wanted, assigned) {
    if (wanted === 0) return current.map(c => ({ ...c, count: 0 }))

    if (assigned === 0) {
        return current.map((c, i) => ({ ...c, count: i === 0 ? wanted : 0 }))
    }

    const scaled = current.map(c => ({ ...c, count: Math.floor((c.count * wanted) / assigned) }))
    let remainder = wanted - scaled.reduce((a, c) => a + c.count, 0)
    if (remainder > 0) {
        // Largest existing column absorbs the rounding leftovers.
        let biggest = 0
        for (let i = 1; i < current.length; i++) {
            if (current[i].count > current[biggest].count) biggest = i
        }
        scaled[biggest].count += remainder
    }
    return scaled
}

function addToColumnTotals(totals, counts, area_by_design, skip = new Set()) {
    for (const c of counts) {
        if (skip.has(c.design)) continue
        const key = columnKey(c.phase, c.batch)
        let entry = totals.get(key)
        if (!entry) {
            entry = { count: 0, m2: 0 }
            totals.set(key, entry)
        }
        entry.count += c.count
        entry.m2 += c.count * (area_by_design.get(c.design) || 0)
    }
}

// Pulls (design, phase, batch) out of the loose payload shapes the callers use.
function countCoords(data) {
    return {
        design: data.design?.uuid || data.design,
        phase: data.phase || data.batch?.phase_uuid,
        batch: data.batch?.uuid || data.batch,
    }
}

// Getter
function getCountFactory(index_key) {
    function getCount() {
        const index = this[index_key]
        return (data) => {
            const { design, phase, batch } = countCoords(data)
            return index.get(countKey(design, phase, batch))
        }
    }
    return getCount
}

// Action
function sumFactory(state_key, index_key, diff) {
    function sumTo(data) {
        const { design, phase, batch } = countCoords(data)
        const c_entry = this[index_key].get(countKey(design, phase, batch))
        if (!c_entry) {
            // No row yet (nothing seeded this cell): create it rather than
            // silently swallowing the click.
            if (!design) return
            const to = Math.max(0, diff)
            this[state_key].push({ phase, batch, design, count: to })
            this.recordEdit({ state_key, index_key, design, phase, batch, from: 0, to })
            this.markDirty()
            return
        }
        const from = c_entry.count
        c_entry.count += diff
        // Don't go below 0 xD
        if (c_entry.count < 0) c_entry.count = 0
        if (c_entry.count === from) return
        this.recordEdit({ state_key, index_key, design, phase, batch, from, to: c_entry.count })
        this.markDirty()

    }
    return sumTo
}

// Action
function putCountFactory(state_key, index_key) {
    // THIS IS ACTUALLY A PUT-OR-NEW
    function putCount(data) {
        const { design, phase, batch } = countCoords(data)
        const count = data.count
        const count_obj = this[index_key].get(countKey(design, phase, batch))
        const from = count_obj ? count_obj.count : 0
        if (count_obj) {
            if (count_obj.count === count) return
            count_obj.count = count
        } else {
            this[state_key].push({ phase, design, batch, count });
        }
        this.recordEdit({ state_key, index_key, design, phase, batch, from, to: count })
        this.markDirty()
    }
    return putCount
}


// Action
function generatorFactory(level, store_key_b, store_key_p, actual_name) {
    // `spatial` holds the decisions of the dialog's Advanced table on
    // buildings and storeys; the ones left out take the default.
    function generator(test_mode, spatial = []) {
        const selected_store = useSelectedStore()
        const constructibles_store = useConstructiblesStore()
        if (test_mode)
            console.log("[i] CONSTRUCTIBLE GENERATOR TEST MODE:")
        else
            console.log("[i] CONSTRUCTIBLE GENERATOR REAL ACTION MODE:")
        const t = useTranslationsStore().translate
        let gen_orders = []

        // Counts naming a design or a batch that no longer exists are ignored
        // everywhere below: they cannot be checked, they cannot be generated, and
        // sending them made the backend answer the whole POST with a 404.
        const stale = this.staleCounts(level)
        const is_stale = (state_key, c) =>
            stale.keys[state_key].has(countKey(c.design, c.phase, c.batch))

        // Check the counts: for every (design, phase) the batch quantities must add
        // up to the phase quantity, keeping WHAT does not add up so it can be shown.
        const project = selected_store.selected_project
        const phases = projectPhases(project)
        const { designs: d_uuid_set, mismatches: raw } = countMismatches(
            this[store_key_b].filter(c => !is_stale(store_key_b, c)),
            this[store_key_p].filter(c => !is_stale(store_key_p, c)),
            phases.map(p => p.uuid),
        )
        const name_of_phase = new Map(phases.map(p => [p.uuid, p.name]))
        const mismatches = raw.map(m => ({
            ...m,
            design_name: constructibles_store.designNameByUuid(m.design),
            phase_name: name_of_phase.get(m.phase) || "",
        }))
        const counts_ok = mismatches.length == 0


        // How many units exist already, so only the difference is generated.
        let existent_actuals = constructibles_store[actual_name]
            .filter(ass => d_uuid_set.has(ass.design))

        if (!counts_ok) {
            bus.emit("notification", {
                timeout: 4000,
                text: `${t("counts_do_not_match")}: ${mismatches.length}`,
                color: "error"
            });
            // Reject WITH the detail, so the dialog can list the offending rows
            // instead of showing a generic "please review your setup".
            return Promise.reject({ mismatches })
        }

        // Pressing Generate is what discards the stale counts, whether or not there
        // is anything else to do — the "nothing to do" case is exactly the one where
        // the plan was made of stale rows only, and it has to clear too.
        let dropped = 0
        if (!test_mode && stale.entries) dropped = this.dropStaleCounts(level)

        // Per (design, batch), in one pass.
        const existent_by_slot = new Map()
        for (const actual of existent_actuals) {
            const key = `${actual.design}|${actual.batch ?? ""}`
            existent_by_slot.set(key, (existent_by_slot.get(key) || 0) + 1)
        }

        // Build the orders.
        this[store_key_b].forEach(c_entry => {
            if (is_stale(store_key_b, c_entry)) return
            const key = `${c_entry.design}|${c_entry.batch ?? ""}`
            const existent_count = existent_by_slot.get(key) || 0
            let to_be_generated_count = c_entry.count - existent_count
            if (to_be_generated_count != 0) {
                gen_orders.push({
                    design: c_entry.design,
                    batch: c_entry.batch,
                    count: to_be_generated_count
                })
            }
        })

        // Notify that nothing changed. With stale counts just discarded, "nothing
        // to do" would be a lie: the plan did change, it simply holds no units to
        // create or delete — and this is the path that unblocks a stuck project.
        if (gen_orders.length == 0) {
            bus.emit("notification", {
                timeout: dropped ? 4000 : 2000,
                text: dropped
                    ? `${t("stale_counts_discarded")}: ${dropped}`
                    : t("generate_nothing_to_do"),
                color: "warning",
            });
            return Promise.resolve(gen_orders)
        } else if (test_mode) {
            return Promise.resolve(gen_orders)
        } else {
            return backend_api_v2.post(
                `company/projects/${project.uuid}/quantities/generate/`,
                { level, orders: gen_orders, spatial })
                .then(({ data }) => {
                    // The backend cleared the level's saved plan in the same
                    // transaction; the matrix rebuilds from the units.
                    this.forgetPlan([level])
                    bus.emit("notification", {
                        timeout: 2000,
                        text: `${t("created")}: ${data.created.length} · ${t("deleted")}: ${data.deleted.length}`,
                        color: "success"
                    });
                    return data
                })
                .catch(e => {
                    // Must NOT be swallowed: the caller decides whether to clear the
                    // saved plan, and it may only do so when this actually succeeded.
                    console.log(`Cant generate ${actual_name}: ${e}`)
                    // The backend says WHY it refused ("Design does not exist",
                    // "Batch does not exist"). Hiding it behind a generic message
                    // is what made a stuck project impossible to diagnose.
                    const reason = e?.response?.data?.status
                    bus.emit("notification", {
                        timeout: 6000,
                        text: reason
                            ? `${t("generate_failed")} (${reason})`
                            : t("generate_failed"),
                        color: "error",
                    });
                    throw e
                })
        }
    }
    return generator
}

// Getter
// Row sum per design. Built from the index in one pass instead of a full .filter()
// of the count array per row.
function getProjectTotalFactory(index_key) {
    function getTotals() {
        const by_design = new Map()
        for (const c of this[index_key].values()) {
            by_design.set(c.design, (by_design.get(c.design) || 0) + c.count)
        }
        return (design) => by_design.get(design?.uuid || design) || 0
    }
    return getTotals
}
